import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Retail Solutions — internal route /solutions/industries/retail
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the Insurance / Real Estate industry subpages. Each video slot
 * is an empty champagne-gold-bordered placeholder frame labeled "Video
 * Demo Coming Soon", alternating sides (video right, then left, then
 * right). After the three sections the page goes directly to the site's
 * standard footer, which is rendered globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="retail-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Retail demo video"
        testId={`retail-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Customer Engagement',
    cloudflareVideoId: '8c0535ed46245ab9ac40e9fe2ed46280',
    heading: 'Every customer question, answered instantly.',
    body:
      'GoogLyn handles order questions, product inquiries, and support requests around the clock \u2014 resolving issues before they turn into lost sales or bad reviews.',
    features: [
      {
        title: '24/7 AI-powered support',
        description: 'Customer questions answered instantly, any time of day.',
      },
      {
        title: 'Real-time order tracking',
        description: 'Status updates delivered without a support ticket.',
      },
      {
        title: 'Personalized recommendations',
        description: 'Upsell and cross-sell suggestions driven by real purchase behavior.',
      },
    ],
  },
  {
    eyebrow: 'Inventory & Fulfillment',
    cloudflareVideoId: 'e3d9c2f13cfeff99fd3a6a5c235e7d36',
    heading: 'Stock visibility, from warehouse to checkout.',
    body:
      'GoogLyn tracks inventory in real time across every channel, automates order processing, and flags restocks before shelves go empty.',
    features: [
      {
        title: 'Real-time inventory visibility',
        description: 'Stock levels synced across every sales channel.',
      },
      {
        title: 'Automated order processing',
        description: 'Orders picked, packed, and routed without manual entry.',
      },
      {
        title: 'Streamlined returns handling',
        description: 'Return requests processed and refunded automatically.',
      },
    ],
  },
  {
    eyebrow: 'Sales & Performance Insights',
    cloudflareVideoId: '7aefe898673284d96c3e386d577401e4',
    heading: 'Demand, predicted before it happens.',
    body:
      "GoogLyn forecasts demand, automates promotions, and surfaces performance data in real time \u2014 so decisions are made on what's actually selling, not last quarter's guesswork.",
    features: [
      {
        title: 'Predictive demand forecasting',
        description: 'Inventory and staffing planned around real demand signals.',
      },
      {
        title: 'Automated promotions',
        description: 'Targeted campaigns triggered by buying patterns.',
      },
      {
        title: 'Real-time performance reporting',
        description: 'Sales data always current, no manual reporting.',
      },
    ],
  },
];

const IndustryRetail = () => {
  return (
    <div className="w-full" data-testid="retail-solutions-page">
      <IndustryPageHeading name="Retail" testId="retail-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`retail-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryRetail;
